<template>
  <div
      class="images-scroll-container"
      @wheel.passive="handleWheel"
      @scroll="handleScroll"
      @touchstart="handleTouchStartWrapper"
      @touchmove.passive="handleTouchMoveWrapper"
      @touchend.passive="handleTouchEndWrapper"
      ref="scrollContainerRef"
      :class="{ minimized: !isExpanded || currentProjectIndex !== projectIndex }"
  >
    <div v-if="!isExpanded && projectLoadingStatus[project.id]?.isLoading" class="thumbnails-spinner-container">
      <div class="spinner"></div>
    </div>
    <div v-else class="images-container" :class="[{ 'fade-in': !projectLoadingStatus[project.id]?.isLoading }, styleVariant]">
      <div
          v-for="image in visibleImages"
          :key="image.id"
          class="image-wrapper"
          :class="[{
            expanded: isExpanded && currentProjectIndex === projectIndex,
            active: isExpanded && currentProjectIndex === projectIndex &&
                   visibleImages.indexOf(image) === currentImageIndex
          }, styleVariant]"
          @click="(event) => handleImageClick(visibleImages.indexOf(image), event)"
      >
        <img
            :src="getImageUrl(image, isExpanded && currentProjectIndex === projectIndex)"
            :alt="project.attributes.name"
            class="gallery-image"
            :class="{ expanded: isExpanded && currentProjectIndex === projectIndex }"
        />
      </div>
    </div>
    <div v-if="isExpanded" class="viewport-overlay">
      <NavigationOverlay
          v-if="isExpanded"
          @navigate="handleNavigate"
      />
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onBeforeUnmount } from 'vue';
import NavigationOverlay from './NavigationOverlay.vue';

const props = defineProps({
  project: {
    type: Object,
    required: true
  },
  projectIndex: {
    type: Number,
    required: true
  },
  isExpanded: {
    type: Boolean,
    required: true
  },
  currentProjectIndex: {
    type: Number,
    default: null
  },
  currentImageIndex: {
    type: Number,
    required: true,
    default: 0
  },
  projectLoadingStatus: {
    type: Object,
    required: true
  },
  visibleImages: {
    type: Array,
    default: () => []
  },
  getImageUrl: {
    type: Function,
    required: true
  },
  handleWheel: {
    type: Function,
    required: true
  },
  handleScroll: {
    type: Function,
    required: true
  },
  handleTouchStart: {
    type: Function,
    required: true
  },
  handleTouchMove: {
    type: Function,
    required: true
  },
  handleTouchEnd: {
    type: Function,
    required: true
  },
  navigate: {
    type: Function,
    required: true
  },
  styleVariant: {
    type: String,
    default: 'default'
  }
});

const emit = defineEmits(['image-selection', 'navigate']);

const scrollContainerRef = ref(null);
const isMobileView = ref(window.innerWidth < 999);

const checkMobileView = () => {
  isMobileView.value = window.innerWidth < 999;
};

const handleNavigate = (direction) => {
  emit('navigate', direction);
};

const handleImageClick = (imageIndex, event) => {
  emit('image-selection', props.projectIndex, imageIndex, event);
};

// Wrapper functions that only call original handlers when expanded
const handleTouchStartWrapper = (event) => {
  if (props.isExpanded && props.currentProjectIndex === props.projectIndex) {
    props.handleTouchStart(event);
  }
};

const handleTouchMoveWrapper = (event) => {
  if (props.isExpanded && props.currentProjectIndex === props.projectIndex) {
    props.handleTouchMove(event);
  }
};

const handleTouchEndWrapper = (event) => {
  if (props.isExpanded && props.currentProjectIndex === props.projectIndex) {
    props.handleTouchEnd(event);
  }
};

onMounted(() => {
  window.addEventListener('resize', checkMobileView);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobileView);
});

defineExpose({
  scrollContainerRef
});
</script>

<style lang="scss" scoped>
.images-scroll-container {
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;
  position: relative;
  max-width: 100vw;
  overscroll-behavior-x: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* When minimized - allow normal scrolling */
  &.minimized {
    touch-action: auto !important;
    overflow-x: auto !important;
    overflow-y: visible !important;
  }

  /* When expanded - horizontal scroll only */
  &:not(.minimized) {
    touch-action: pan-x !important;
    overflow-x: auto;
    overflow-y: hidden;

    .project-row.expanded & {
      flex: 1;
      max-height: 100%;
    }
  }
}

.thumbnails-spinner-container {
  height: calc(15vh - 100px);
  min-height: 70px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.images-container {
  height: calc(15vh - 100px);
  min-height: 70px;
  display: inline-flex;
  gap: 0 !important;
  will-change: transform;
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  min-width: max-content;
  position: relative;
  font-size: 0;
  display: -moz-inline-box;
  display: inline-flex;

  &.main {
    height: calc(25vh - 100px);
    min-height: 200px;
  }

  &.archive {
    height: calc(15vh - 100px);
    min-height: 40px;
  }

  .project-row.expanded & {
    height: 100%;
    max-height: 100%;
  }

  &.fade-in {
    animation: fadeIn 0.5s ease-in-out;
  }
}

.image-wrapper {
  flex: 0 0 auto;
  display: inline-block;
  height: 100%;
  position: relative;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  width: auto;
  font-size: 16px;
  left: 0;
  margin-right: 5px;

  &.main {
    margin-right: 20px;
  }

  &.expanded {
    margin-right: 20px;
    width: auto;
    box-sizing: content-box;
  }

  &:last-child {
    margin-right: 0;
  }
}

@media (max-width: 769px) {
  .image-wrapper {
    margin-right: 2px;

    &.main {
      margin-right: 2px;
    }

    &:last-child {
      margin-right: 0;
    }
  }
}

.gallery-image {
  height: 100%;
  object-fit: cover;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  width: auto;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.viewport-overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
}

.spinner {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s ease infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>